import { logger } from '@/lib/logger';

export type PushPermissionState =
  'unsupported' | 'default' | 'granted' | 'denied';

// Which stage of the push setup failed. Surfaces in the error report so a
// "service worker error" tells us whether to look at SW serving, install
// timing, or push-service connectivity.
export type PushServiceWorkerStep =
  | 'registration'
  | 'ready-timeout'
  | 'subscribe-timeout'
  | 'subscribe-failed'
  | 'vapid-invalid';

// Distinguishable error so callers can surface a service-worker-specific
// message instead of the generic "could not update notifications" — the user
// usually needs to refresh / re-enter the password gate, not retry blindly.
export class PushServiceWorkerError extends Error {
  readonly step?: PushServiceWorkerStep;

  constructor(
    message: string,
    options?: { cause?: unknown; step?: PushServiceWorkerStep }
  ) {
    super(message, options ? { cause: options.cause } : undefined);
    this.name = 'PushServiceWorkerError';
    this.step = options?.step;
  }
}

export function getPushEnableErrorText(
  error: unknown,
  fallback: string,
  serviceWorkerFallback: string,
  pushServiceFallback: string
): string {
  if (error instanceof PushServiceWorkerError) {
    // The SW is fine when subscribe fails — the browser's push service
    // (FCM/autopush/APNs) refused or never answered, typically Brave with
    // Google push messaging off, or a network blocking it. A refresh won't help.
    if (
      error.step === 'subscribe-failed' ||
      error.step === 'subscribe-timeout'
    ) {
      return pushServiceFallback;
    }
    // A malformed VAPID key is our config bug; nothing the user can act on.
    if (error.step === 'vapid-invalid') {
      return fallback;
    }
    return serviceWorkerFallback;
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  if (error && typeof error === 'object') {
    const maybeError = error as {
      status?: number;
      data?: unknown;
      message?: unknown;
    };

    if (typeof maybeError.message === 'string' && maybeError.message) {
      return maybeError.message;
    }

    if (typeof maybeError.data === 'string' && maybeError.data) {
      return maybeError.data;
    }

    if (maybeError.data && typeof maybeError.data === 'object') {
      const data = maybeError.data as { message?: unknown };
      if (typeof data.message === 'string' && data.message) {
        return data.message;
      }
      if (Array.isArray(data.message)) {
        const messages = data.message.filter(
          (item): item is string => typeof item === 'string' && item.length > 0
        );
        if (messages.length > 0) {
          return messages.join(', ');
        }
      }
    }

    if (typeof maybeError.status === 'number') {
      return `Request failed with status ${maybeError.status}`;
    }
  }

  return fallback;
}

export function isPushSupported(): boolean {
  if (typeof window === 'undefined') return false;
  return (
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

export function getPermissionState(): PushPermissionState {
  if (!isPushSupported()) return 'unsupported';
  return Notification.permission as PushPermissionState;
}

export function isValidPushEndpoint(
  endpoint: string | null | undefined
): boolean {
  if (!endpoint) return false;
  try {
    const url = new URL(endpoint);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
}

const SW_READY_TIMEOUT_MS = 8000;

function withTimeout<T>(
  p: Promise<T>,
  ms: number,
  message: string,
  step: PushServiceWorkerStep
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const t = setTimeout(
      () => reject(new PushServiceWorkerError(message, { step })),
      ms
    );
    p.then(
      (v) => {
        clearTimeout(t);
        resolve(v);
      },
      (e) => {
        clearTimeout(t);
        reject(e);
      }
    );
  });
}

// next-pwa is disabled in development, so the Workbox-generated sw.js is
// missing locally. In dev we register the push-only service worker directly.
// In production we prefer /sw.js (which importScripts /push-sw.js to share
// the push handlers), but fall back to /push-sw.js when /sw.js fails — that
// promise never resolves when the SW fetch was 401'd by an Amplify password
// gate or rewritten to HTML by an SPA fallback. /push-sw.js is dependency-
// free and committed to the repo, so it's always available even when the
// Workbox build artifact isn't.
async function ensurePushServiceWorker(): Promise<ServiceWorkerRegistration> {
  const existing = await navigator.serviceWorker.getRegistration('/');
  if (existing?.active) {
    logger.debug('[push]', 'sw: existing registration found');
    return existing;
  }
  if (existing) {
    // Still installing — possibly from a concurrent call — so give it the same
    // grace period. A registration that never activates (stuck install, or a
    // leftover from a failed attempt) would otherwise be returned forever and
    // fail every subscribe; drop it and register fresh.
    try {
      await withTimeout(
        navigator.serviceWorker.ready,
        SW_READY_TIMEOUT_MS,
        'Existing service worker did not become ready',
        'ready-timeout'
      );
      return existing;
    } catch {
      logger.debug(
        '[push]',
        'sw: existing registration never activated, re-registering'
      );
      await existing.unregister().catch(() => {});
    }
  }

  const swCandidates =
    process.env.NODE_ENV === 'development'
      ? ['/push-sw.js']
      : ['/sw/sw.js', '/push-sw.js'];

  let lastError: unknown = null;
  for (const swUrl of swCandidates) {
    logger.debug('[push]', `sw: registering ${swUrl}`);

    let reg: ServiceWorkerRegistration;
    try {
      reg = await navigator.serviceWorker.register(swUrl, { scope: '/' });
    } catch (err) {
      const causeMessage = err instanceof Error ? err.message : String(err);
      logger.debug(
        '[push]',
        `sw: registration failed (${swUrl}): ${causeMessage}`
      );
      lastError = new PushServiceWorkerError(
        `Failed to register service worker at ${swUrl}: ${causeMessage}`,
        { cause: err, step: 'registration' }
      );
      continue;
    }

    try {
      await withTimeout(
        navigator.serviceWorker.ready,
        SW_READY_TIMEOUT_MS,
        `Service worker ${swUrl} did not become ready within ${SW_READY_TIMEOUT_MS}ms`,
        'ready-timeout'
      );
    } catch (err) {
      logger.debug('[push]', `sw: ready timed out for ${swUrl}`);
      // Unregister the stuck SW so the next candidate starts from a clean
      // scope — otherwise the browser keeps a "redundant" registration and
      // serviceWorker.ready may resolve to it on the next attempt.
      await reg.unregister().catch(() => {});
      lastError = err;
      continue;
    }

    logger.debug('[push]', `sw: ready (${swUrl})`);
    return reg;
  }

  // Preserve the typed error for the toggle's user-facing message routing.
  if (lastError instanceof PushServiceWorkerError) {
    throw lastError;
  }
  const causeMessage =
    lastError instanceof Error ? lastError.message : String(lastError);
  throw new PushServiceWorkerError(
    `Failed to register any service worker (tried ${swCandidates.join(', ')}): ${causeMessage}`,
    { cause: lastError, step: 'registration' }
  );
}

export async function getCurrentSubscription(): Promise<PushSubscription | null> {
  if (!isPushSupported()) return null;
  try {
    const reg = await ensurePushServiceWorker();
    return await reg.pushManager.getSubscription();
  } catch (err) {
    // Don't surface SW failures to passive callers (auto-resync, status
    // checks). `capturePushError` is the report — it carries the push.step /
    // push.callsite context — so the companion log stays on the dev console.
    capturePushError(err, 'getCurrentSubscription');
    logger.debug('[push] getCurrentSubscription failed:', err);
    return null;
  }
}

// JSON.stringify throws on a circular object; this runs inside catch handlers.
const safeStringify = (value: object): string => {
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
};

// Reports a push failure through the logger (-> Faro) with a stable label and
// the push.step / push.callsite context, so PushServiceWorkerError reports can
// be filtered by which stage failed and which entry point hit it.
export function capturePushError(err: unknown, callsite: string): void {
  // The visitor dismissed or refused the browser permission prompt: a choice,
  // not a failure, so there is nothing to report.
  if (
    err instanceof Error &&
    /^Notification permission (default|denied)$/.test(err.message)
  ) {
    return;
  }
  // One string: Faro flattens object arguments to "[object Object]".
  const step = err instanceof PushServiceWorkerError ? ` step=${err.step}` : '';
  const reason =
    err instanceof Error
      ? `${err.name}: ${err.message}`
      : typeof err === 'object' && err !== null
        ? safeStringify(err)
        : String(err);
  logger.error(`[push] error callsite=${callsite}${step} ${reason}`);
}

// Decoded VAPID public key shape: uncompressed P-256 point = 65 bytes.
// Anything else means the env var is malformed (private key pasted, trailing
// whitespace stripped one too many chars, wrong curve, etc).
const VAPID_PUBLIC_KEY_BYTES = 65;

function decodeVapidPublicKey(rawKey: string): Uint8Array {
  // Trim whitespace — copy-paste from a key generator often leaves a trailing
  // newline that breaks the base64 decode silently.
  const trimmed = rawKey.trim();
  if (!trimmed) {
    throw new PushServiceWorkerError(
      'NEXT_PUBLIC_VAPID_PUBLIC_KEY is empty after trim — env var likely missing.',
      { step: 'vapid-invalid' }
    );
  }
  let bytes: Uint8Array;
  try {
    bytes = urlBase64ToUint8Array(trimmed);
  } catch (err) {
    throw new PushServiceWorkerError(
      'NEXT_PUBLIC_VAPID_PUBLIC_KEY could not be base64-decoded — check for whitespace or wrong format.',
      { cause: err, step: 'vapid-invalid' }
    );
  }
  if (bytes.length !== VAPID_PUBLIC_KEY_BYTES) {
    throw new PushServiceWorkerError(
      `VAPID public key has wrong size (${bytes.length} bytes; expected ${VAPID_PUBLIC_KEY_BYTES}). Check that NEXT_PUBLIC_VAPID_PUBLIC_KEY is the public key (starts with "B"), not the private key, and matches the backend VAPID_PUBLIC_KEY.`,
      { step: 'vapid-invalid' }
    );
  }
  return bytes;
}

async function pushManagerSubscribe(
  reg: ServiceWorkerRegistration,
  applicationServerKey: Uint8Array
): Promise<PushSubscription> {
  return withTimeout(
    reg.pushManager.subscribe({
      userVisibleOnly: true,
      // PushManager wants a BufferSource; the lib.dom typings disagree about
      // SharedArrayBuffer subtyping under strict mode, so widen explicitly.
      applicationServerKey: applicationServerKey as BufferSource
    }),
    SW_READY_TIMEOUT_MS,
    `pushManager.subscribe did not resolve within ${SW_READY_TIMEOUT_MS}ms`,
    'subscribe-timeout'
  );
}

export async function subscribeUser(
  vapidPublicKeyOverride?: string
): Promise<PushSubscription> {
  if (!isPushSupported()) {
    throw new Error('Push notifications are not supported in this browser.');
  }

  // Prefer the override (fetched live from the backend so it always matches
  // the server's signing key). Fall back to the build-time env so existing
  // deploys keep working until the new endpoint is reachable everywhere.
  const vapidPublicKey =
    vapidPublicKeyOverride ?? process.env.NEXT_PUBLIC_VAPID_PUBLIC_KEY;
  if (!vapidPublicKey) {
    throw new Error(
      'No VAPID public key available. The server endpoint is unreachable and NEXT_PUBLIC_VAPID_PUBLIC_KEY is not configured.'
    );
  }
  // Validate before requesting permission — no point in burning the user's
  // permission prompt if the key is broken.
  const applicationServerKey = decodeVapidPublicKey(vapidPublicKey);

  logger.debug('[push]', 'subscribeUser: requesting permission');
  const permission = await Notification.requestPermission();
  logger.debug('[push]', `subscribeUser: permission=${permission}`);
  if (permission !== 'granted') {
    throw new Error(`Notification permission ${permission}`);
  }

  const reg = await ensurePushServiceWorker();
  const existing = await reg.pushManager.getSubscription();
  if (existing) {
    logger.debug('[push]', 'subscribeUser: reusing existing subscription');
    return existing;
  }

  logger.debug('[push]', 'subscribeUser: pushManager.subscribe');
  try {
    const subscription = await pushManagerSubscribe(reg, applicationServerKey);
    logger.debug('[push]', 'subscribeUser: subscribed');
    return subscription;
  } catch (firstErr) {
    // Browsers reject with strings like "Registration failed - push service
    // error" when the push service (FCM/Mozilla autopush/APNs) refuses the
    // call. The most common recoverable cause is a stale half-state on the
    // PushManager — try once with a clean slate before giving up.
    const firstMessage =
      firstErr instanceof Error ? firstErr.message : String(firstErr);
    logger.debug(
      '[push]',
      `subscribeUser: subscribe rejected: ${firstMessage}`
    );

    // Don't retry on timeout — the SW promise machinery is already wedged.
    if (
      firstErr instanceof PushServiceWorkerError &&
      firstErr.step === 'subscribe-timeout'
    ) {
      throw firstErr;
    }

    const stale = await reg.pushManager.getSubscription();
    if (stale) {
      await stale.unsubscribe().catch(() => {});
      logger.debug(
        '[push]',
        'subscribeUser: cleared stale subscription, retrying'
      );
    }

    try {
      const subscription = await pushManagerSubscribe(
        reg,
        applicationServerKey
      );
      logger.debug('[push]', 'subscribeUser: subscribed on retry');
      return subscription;
    } catch (retryErr) {
      const retryMessage =
        retryErr instanceof Error ? retryErr.message : String(retryErr);
      throw new PushServiceWorkerError(
        `pushManager.subscribe failed: ${retryMessage}`,
        { cause: retryErr, step: 'subscribe-failed' }
      );
    }
  }
}

export async function unsubscribeUser(): Promise<string | null> {
  if (!isPushSupported()) return null;
  const reg = await ensurePushServiceWorker();
  const subscription = await reg.pushManager.getSubscription();
  if (!subscription) return null;
  const endpoint = subscription.endpoint;
  await subscription.unsubscribe();
  return endpoint;
}

export function getSubscriptionKeys(subscription: PushSubscription): {
  p256dh: string;
  auth: string;
} {
  const json = subscription.toJSON();
  const keys = json.keys || ({} as Record<string, string>);
  return { p256dh: keys.p256dh || '', auth: keys.auth || '' };
}

export function hasValidSubscriptionMaterial(
  subscription: PushSubscription
): boolean {
  if (!isValidPushEndpoint(subscription.endpoint)) return false;
  const { p256dh, auth } = getSubscriptionKeys(subscription);
  return !!p256dh && !!auth;
}

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) out[i] = raw.charCodeAt(i);
  return out;
}
